import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import styles from './MobileMenuItem.scss';
import { offersCategory } from '../offers-category';
import { notifyAnalytics } from '../notify-analytics';
import { formatTestId } from '../../../utils/test-id/format-testid';
import { filterMenuData } from '../../../utils/menu/filterMenuData';
import { ArrowRightOutlined } from 'jl-design-system/components/icons/ArrowRightOutlined';
import { thumbnailMap } from './thumbnailMap';

export const MobileMenuItem = ({
  child = undefined,
  children = undefined,
  className = '',
  hierarchy: parentHierarchy = [],
  isOffersRemoveViewMoreActive = false,
  label,
  onClick = undefined,
  type = '',
  url = '',
  mobileAccordionActive = false,
  mobileNavThumbnailsActive = false,
  activeTab = '',
}) => {
  const isButton = child?.length > 0;
  const isHeading = type === 'MENUITEM';
  const isBrandHeading = type === 'BRANDMENUITEM';
  const isAnchor = url && !isButton;
  const isOffer = offersCategory(label, 1);
  const isMoneySection =
    isHeading && label.toLowerCase() === 'john lewis money';
  const isShopOrHelpTab = activeTab === 'Shop' || activeTab === 'Help';

  const renderThumbnailImage = itemName => {
    if (
      mobileNavThumbnailsActive &&
      activeTab &&
      thumbnailMap[activeTab] &&
      thumbnailMap[activeTab][itemName]
    ) {
      const thumbnailData = thumbnailMap[activeTab][itemName];
      return (
        <img
          src={thumbnailData.src}
          srcSet={thumbnailData.srcset}
          alt="" // Deliberately empty alt text as these are decorative images
          className={styles.mobileMenuItem__thumbnailImage}
          width="48"
          height="48"
          data-testid={`nav-thumbnail-${itemName.toLowerCase().replace(/\s+/g, '-')}`}
        />
      );
    }
    return null;
  };

  // Check if we should show a thumbnail instead of chevron
  const thumbnailImage = renderThumbnailImage(label);
  const shouldShowThumbnail =
    isButton && !isHeading && !isBrandHeading && thumbnailImage;

  const offersChildren =
    filterMenuData(child, ['MOBILE_WEB'])?.filter(item => !!item.url) || [];
  const hierarchy = [...parentHierarchy, label];
  const sendAnalytics = (tail = []) => {
    notifyAnalytics([...hierarchy, ...tail].join('|'));
  };

  const advanceToChild = e => {
    sendAnalytics();
    onClick(e);
  };

  if (!isAnchor && !isButton && !isHeading && !isBrandHeading) {
    return <></>;
  }

  const getMoreLinkName = moreLabel =>
    `More ${moreLabel
      .toLowerCase()
      .replace(/john lewis/gi, 'John Lewis')
      .replace('our top brands', 'top brands')}`;

  return (
    <>
      <li
        className={classNames(styles.mobileMenuItem, className, {
          [styles['mobileMenuItem--isShopOrHelpTab']]: isShopOrHelpTab,
        })}
      >
        {isAnchor && (
          <a
            href={url}
            className={classNames(styles.mobileMenuItem__link, {
              [styles.mobileMenuItem__link__bold]: mobileAccordionActive,
            })}
            onClick={() => sendAnalytics([])}
            data-testid={formatTestId(label, 'nav-')}
            data-hierarchy={parentHierarchy.join('|')}
          >
            {label}
          </a>
        )}
        {isButton && !isHeading && !isBrandHeading && (
          <button
            onClick={advanceToChild}
            data-testid={formatTestId(label, 'nav-')}
            className={classNames(styles.mobileMenuItem__button, {
              [styles.mobileMenuItem__button__nonBoldButton]:
                !mobileAccordionActive,
              [styles.mobileMenuItem__chevron]:
                !mobileAccordionActive && !shouldShowThumbnail,
              [styles.mobileMenuItem__arrow]:
                mobileAccordionActive && !shouldShowThumbnail,
              [styles.mobileMenuItem__thumbnail]: shouldShowThumbnail,
              [styles.offersLabel]: isOffer,
            })}
            aria-label={
              label.endsWith('*')
                ? `${label.replace('*', '')} selected lines only`
                : label
            }
          >
            {label}
            {mobileAccordionActive && !shouldShowThumbnail && (
              <ArrowRightOutlined size="medium" />
            )}
            {thumbnailImage}
          </button>
        )}
        {isButton && (isHeading || isBrandHeading) && (
          <div
            className={classNames(styles.buttonHeading, {
              [styles.buttonHeading__moneyHeader]: isMoneySection,
            })}
          >
            <h4>
              {label === 'top brands' ? 'Our ' : ''}
              {label}
            </h4>
          </div>
        )}
        {isOffer && offersChildren.length > 0 && (
          <ul className={styles.offersSubMenu}>
            {offersChildren.slice(0, 3).map(nestedItem => (
              <li className={styles.mobileMenuItem} key={nestedItem.name}>
                <a
                  className={classNames(styles.mobileMenuItem__link)}
                  onClick={() => sendAnalytics([nestedItem.name])}
                  href={nestedItem.url}
                >
                  {nestedItem.name}
                </a>
              </li>
            ))}
            {!isOffersRemoveViewMoreActive && offersChildren.length > 3 && (
              <li
                className={classNames(
                  styles.mobileMenuItem,
                  styles.mobileMenuItem__viewMoreLink
                )}
              >
                <button
                  className={classNames(styles.mobileMenuItem__button)}
                  onClick={advanceToChild}
                >
                  View more
                </button>
              </li>
            )}
          </ul>
        )}
        {isHeading && (
          <ul className={styles.headingSubMenu} data-testid="heading-sub-menu">
            {offersChildren.map(nestedItem => {
              const nestedThumbnailImage = renderThumbnailImage(
                nestedItem.name
              );

              return (
                <li className={styles.mobileMenuItem} key={nestedItem.name}>
                  <a
                    className={classNames(
                      styles.mobileMenuItem__link,
                      styles.mobileMenuItem__link__tabbedBrandsLink,
                      {
                        [styles.mobileMenuItem__link__withThumbnail]:
                          !!nestedThumbnailImage,
                      }
                    )}
                    onClick={() => sendAnalytics([nestedItem.name])}
                    href={nestedItem.url}
                    target={isMoneySection ? '_blank' : undefined}
                    rel={isMoneySection ? 'noreferrer' : undefined}
                    data-testid={formatTestId(nestedItem.name, 'services-')}
                  >
                    {nestedItem.name}
                    {nestedThumbnailImage}
                  </a>
                </li>
              );
            })}
          </ul>
        )}
        {isBrandHeading && (
          <ul className={styles.headingSubMenu}>
            {offersChildren.slice(0, 3).map(nestedItem => {
              const nestedThumbnailImage = renderThumbnailImage(
                nestedItem.name
              );

              return (
                <li
                  className={classNames(styles.mobileMenuItem)}
                  key={nestedItem.name}
                >
                  <a
                    className={classNames(
                      styles.mobileMenuItem__link,
                      styles.mobileMenuItem__link__tabbedBrandsLink,
                      {
                        [styles.mobileMenuItem__link__withThumbnail]:
                          !!nestedThumbnailImage,
                      }
                    )}
                    onClick={() => sendAnalytics([nestedItem.name])}
                    href={nestedItem.url}
                    data-testid={formatTestId(nestedItem.name, 'brands-')}
                  >
                    {nestedItem.name}
                    {nestedThumbnailImage}
                  </a>
                </li>
              );
            })}
            <li className={classNames(styles.mobileMenuItem)}>
              <button
                onClick={advanceToChild}
                data-testid={formatTestId(label, 'nav-')}
                className={classNames(
                  styles.mobileMenuItem__button,
                  styles.mobileMenuItem__chevron,
                  styles.mobileMenuItem__button__nonBoldButton,
                  styles.mobileMenuItem__chevron__tabbedBrandsChevron
                )}
              >
                {getMoreLinkName(label)}
              </button>
            </li>
          </ul>
        )}
        {children}
      </li>
    </>
  );
};

MobileMenuItem.propTypes = {
  label: PropTypes.string.isRequired,
  url: PropTypes.string,
  className: PropTypes.string,
  onClick: PropTypes.func,
  child: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      url: PropTypes.string,
    })
  ),
  hierarchy: PropTypes.arrayOf(PropTypes.string),
  children: PropTypes.node,
  isOffersRemoveViewMoreActive: PropTypes.bool,
  mobileAccordionActive: PropTypes.bool,
  mobileNavThumbnailsActive: PropTypes.bool,
  type: PropTypes.string,
  activeTab: PropTypes.string,
};
